import React, { useState } from 'react';
import { Sparkles, MapPin, Users, Heart, Waves, Crown } from 'lucide-react';

/**
 * ChatStarters v2 — premium concierge suggestion rail.
 *
 * Previously chip pills in a bordered rounded box. Now: a labeled
 * horizontal card row where each card has an icon, the style label,
 * and a "Try this →" affordance. Reads like a concierge's suggestion
 * menu, not a chatbot's auto-complete.
 *
 * Business rationale:
 *   • Airbnb / ChatGPT Explore pattern — visual chunks feel less like
 *     "type into a search bar" and more like "pick from a curated menu".
 *   • Icons signal category so users don't have to read every label.
 *   • Reduces choice paralysis by showing 3 above the fold + "See more"
 *     for the rest.
 */
const STYLE_ICONS = {
  hidden:   MapPin,
  family:   Users,
  romantic: Heart,
  beach:    Waves,
  luxury:   Crown,
};

const STYLE_TONE = {
  hidden:   { bg: 'bg-brand-amber-50', text: 'text-brand-amber-800', border: 'border-brand-amber-200', icon: 'text-brand-amber-700' },
  family:   { bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-100',   icon: 'text-blue-600' },
  romantic: { bg: 'bg-rose-50',   text: 'text-rose-700',   border: 'border-rose-100',   icon: 'text-rose-600' },
  beach:    { bg: 'bg-cyan-50',   text: 'text-cyan-700',   border: 'border-cyan-100',   icon: 'text-cyan-600' },
  luxury:   { bg: 'bg-amber-50',  text: 'text-amber-800',  border: 'border-amber-100',  icon: 'text-amber-600' },
};

export default function ChatStarters({
  starters,
  title,
  showMoreLabel,
  showLessLabel,
  initialVisible = 3,
  loading,
  onStarterClick,
}) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? starters : starters.slice(0, initialVisible);

  return (
    <div className="mt-3" data-testid="chat-starters">
      <div className="flex items-center gap-1.5 mb-2 text-[11px] uppercase tracking-widest font-bold text-gray-500 px-1">
        <Sparkles className="w-3 h-3 text-blue-600" />
        {title}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {visible.map(([label, styleKey], idx) => {
          const Icon = STYLE_ICONS[styleKey] || Sparkles;
          const tone = STYLE_TONE[styleKey] || STYLE_TONE.hidden;
          return (
            <button
              key={`starter-${idx}`}
              onClick={() => onStarterClick(label, styleKey)}
              disabled={loading}
              data-testid={`chat-starter-${idx}`}
              className={`group flex items-start gap-2.5 p-3 rounded-xl bg-white border ${tone.border} hover:border-blue-300 hover:shadow-md transition-all text-left disabled:opacity-50 disabled:pointer-events-none`}
            >
              <div className={`w-8 h-8 rounded-lg ${tone.bg} flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-4 h-4 ${tone.icon}`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-gray-900 leading-snug">
                  {label}
                </div>
                <div className={`text-[11px] font-semibold ${tone.text} mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity`}>
                  Try this →
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {starters.length > initialVisible && (
        <div className="mt-2 flex justify-center">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            data-testid="chat-starters-toggle"
            className="text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors"
          >
            {showAll ? showLessLabel : showMoreLabel}
          </button>
        </div>
      )}
    </div>
  );
}
